class JournalStory extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-journal-content]');
    this.progress = this.querySelector('[data-journal-progress]');
    this.toc = this.querySelector('[data-journal-toc]');
    if (!this.content) return;

    this.headings = [...this.content.querySelectorAll('h2')];
    this.applyEditorialCorrections();
    this.buildChapters();
    this.markImageTreatments();
    this.buildContents();
    this.decorateChapters();

    this.onScroll = this.update.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  applyEditorialCorrections() {
    const replacements = new Map([
      ['A Christmas in Gensac, Where Cannelé Began', 'A Christmas in France, Where Cannelé Began'],
      ['The Cannelé engagement ring, handcrafted in the UK', 'The Cannelé engagement ring and wedding ring, handcrafted in the UK'],
      ['The Cannelé collection - diamond, ruby, chocolate diamond, in rose gold, white gold and platinum', 'Some members of the Cannelé collection']
    ]);

    this.content.querySelectorAll('h2, figure em, figcaption, p > em:only-child').forEach((element) => {
      const corrected = replacements.get(element.textContent.trim());
      if (corrected) element.textContent = corrected;
    });

    this.content.querySelectorAll('p').forEach((paragraph) => {
      paragraph.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.includes('hand-finished flutings')) {
          node.textContent = node.textContent.replaceAll('hand-finished flutings', 'hand-engraved flutings');
        }
      });
    });
  }

  markImageTreatments() {
    this.content.querySelectorAll('img').forEach((image) => {
      const alt = (image.alt || '').toLowerCase();
      const source = (image.getAttribute('src') || '').toLowerCase();

      if (
        alt.includes('early design sketches') ||
        alt.includes('cannele unique engagement ring and matching wedding ring worn on model') ||
        alt.includes('cannele engagement ring collection in different stones and metals') ||
        alt.includes('cannele blue sapphire diamond halo engagement ring yellow gold')
      ) {
        (image.closest('figure') || image.parentElement)?.classList.add('journal-story__narrow-editorial-image');
      }

      if (alt.includes('the cannelé engagement ring, handcrafted in the uk') || source.includes('cannele_halo_engagement_ring_and_wedding_ring')) {
        image.src = this.dataset.bodyPairSrc;
        image.removeAttribute('srcset');
        image.alt = 'Cannelé diamond engagement ring with its matching curved diamond wedding ring in platinum';
        const caption = image.parentElement?.nextElementSibling?.querySelector('em');
        if (caption) caption.textContent = 'The Cannelé engagement ring and matching wedding ring, handcrafted in the UK';
      }

      if (alt.includes('cannele diamond engagement ring yellow gold showing fluted setting') || source.includes('cannele-33-yellow-gold-diamond')) {
        image.src = this.dataset.yellowGoldSrc;
        image.removeAttribute('srcset');
        image.alt = 'Cannelé engagement ring in 18ct yellow gold with a blue sapphire centre and diamond halo, shown from the front and rear';
        const caption = image.closest('figure')?.querySelector('em, figcaption');
        if (caption) caption.textContent = 'Cannelé in 18ct yellow gold with blue sapphire and diamond halo';
      }
    });

    this.content.querySelectorAll('figure').forEach((figure) => {
      const image = figure.querySelector('img');
      const description = `${image?.alt || ''} ${figure.textContent || ''}`.toLowerCase();
      if (description.includes('the cannelé engagement ring and wedding ring, handcrafted in the uk')) {
        image.src = this.dataset.bodyPairSrc;
        image.removeAttribute('srcset');
        image.alt = 'Cannelé diamond engagement ring with its matching curved diamond wedding ring in platinum';
        const caption = figure.querySelector('em, figcaption');
        if (caption) caption.textContent = 'The Cannelé engagement ring and matching wedding ring, handcrafted in the UK';
      }
      if (description.includes('cannelé twist in a range') || description.includes('twist engagement ring collection group')) {
        image.src = this.dataset.twistGroupSrc;
        image.removeAttribute('srcset');
        image.alt = 'Cannelé Twist engagement rings in platinum and rose gold with diamonds, chocolate diamond and ruby';
        const caption = figure.querySelector('em, figcaption');
        if (caption) caption.textContent = 'Cannelé Twist engagement rings in platinum and rose gold with diamonds, chocolate diamond and ruby.';
        figure.classList.add('journal-story__matched-product-image');
      }
      if (description.includes('cannelé twist - diamond, platinum')) {
        figure.classList.add('journal-story__matched-product-image');
      }
      if (description.includes('twist stone set diamond wedding ring')) {
        image.src = this.dataset.pairingSrc;
        image.removeAttribute('srcset');
        image.alt = 'Cannelé Twist engagement ring with the Cannelé wedding ring, worn together';
        const caption = figure.querySelector('em, figcaption');
        if (caption) caption.textContent = 'Cannelé Twist engagement ring with the Cannelé wedding ring, worn together';
      }
      if (description.includes('cannelé engagement ring with its matching wedding ring')) {
        image.alt = 'Cannelé Twist engagement ring with the Cannelé wedding ring, worn together';
        const caption = figure.querySelector('em, figcaption');
        if (caption) caption.textContent = 'Cannelé Twist engagement ring with the Cannelé wedding ring, worn together';
      }
      if (description.includes('cannele engagement ring collection in different stones and metals') || description.includes('some members of the cannelé collection')) {
        image.alt = 'Some members of the Cannelé collection in platinum, rose gold and white gold with diamond, ruby and chocolate diamond centres';
        const caption = figure.querySelector('em, figcaption');
        if (caption) caption.textContent = 'Some members of the Cannelé collection';
      }
    });

    this.content.querySelectorAll('div').forEach((container) => {
      const directFigures = [...container.children].filter((child) => child.tagName === 'FIGURE');
      if (directFigures.length > 1) container.classList.add('journal-story__image-pair');
    });
  }

  buildChapters() {
    const firstHeading = this.headings[0];
    if (!firstHeading) return;

    const intro = document.createElement('div');
    intro.className = 'journal-story__intro';
    while (this.content.firstChild && this.content.firstChild !== firstHeading) {
      intro.appendChild(this.content.firstChild);
    }
    this.content.insertBefore(intro, firstHeading);

    this.headings.forEach((heading, index) => {
      const chapter = document.createElement('section');
      chapter.className = `journal-chapter journal-chapter--${(index % 4) + 1}`;
      const inner = document.createElement('div');
      inner.className = 'journal-chapter__inner';
      heading.parentNode.insertBefore(chapter, heading);
      chapter.appendChild(inner);
      inner.appendChild(heading);

      let node = chapter.nextSibling;
      while (node && !(node.nodeType === 1 && node.tagName === 'H2')) {
        const next = node.nextSibling;
        inner.appendChild(node);
        node = next;
      }
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  buildContents() {
    if (!this.toc || !this.headings.length) return;
    const list = document.createElement('ol');
    this.headings.forEach((heading, index) => {
      heading.id ||= `chapter-${index + 1}`;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
    });
    this.toc.appendChild(list);
  }

  decorateChapters() {
    this.headings.forEach((heading, index) => {
      heading.dataset.chapter = `Chapter ${String(index + 1).padStart(2, '0')}`;
    });
  }

  update() {
    const rect = this.getBoundingClientRect();
    const total = Math.max(this.offsetHeight - window.innerHeight, 1);
    const progress = Math.min(Math.max(-rect.top / total, 0), 1);
    if (this.progress) this.progress.style.width = `${progress * 100}%`;

    if (!this.toc) return;
    let active = this.headings[0]?.id;
    this.headings.forEach((heading) => {
      if (heading.getBoundingClientRect().top <= window.innerHeight * .36) active = heading.id;
    });
    this.toc.querySelectorAll('a').forEach((link) => {
      link.setAttribute('aria-current', link.hash === `#${active}` ? 'true' : 'false');
    });
  }
}

if (!customElements.get('journal-story')) {
  customElements.define('journal-story', JournalStory);
}
